import {
  CancelledError,
  QueryClient,
  type MutationObserverOptions,
  type VueQueryPluginOptions
} from '@tanstack/vue-query';
import { isAxiosError } from 'axios';

const staleTime = Number(import.meta.env.VITE_QUERY_GC_TIME_SECONDS || 86_400) * 1000;
const retryDelayTime = Number(import.meta.env.VITE_QUERY_RETRY_DELAY_TIME_SECONDS || 1) * 1000;
const retryCount = Number(import.meta.env.VITE_QUERY_RETRY_COUNT || 3);

const handleThrowOnError: MutationObserverOptions['throwOnError'] = (error) => {
  if (error instanceof CancelledError) {
    return false;
  }
  return true;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (error instanceof CancelledError) {
          return true; // Always retry for cancelled queries
        } else if (!isAxiosError(error)) {
          return false;
        } else if (
          [401, 404, 422].includes(error.response?.status ?? -1) ||
          error.config?.url?.endsWith('/auth/refresh')
        ) {
          return false;
        }
        return failureCount <= retryCount;
      },
      retryDelay(failureCount) {
        return Math.min(retryDelayTime ** failureCount, 30_000);
      },
      networkMode: 'offlineFirst',
      staleTime: Number.isNaN(staleTime) ? 1000 * 60 * 60 * 24 : staleTime,
      throwOnError: handleThrowOnError
    },
    mutations: {
      networkMode: 'offlineFirst',
      throwOnError: handleThrowOnError
    }
  }
});
const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClient: queryClient
};

export default vueQueryPluginOptions;
